import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserPassword } from '../../services/pocketbase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

// Replaced FiBuilding with FiBriefcase
const { FiUser, FiMail, FiShield, FiBriefcase, FiLock, FiSave, FiEye, FiEyeOff } = FiIcons;

const Profile = () => {
  const { currentUser, currentUserEmail, currentUserRole, currentTenantName, currentUserId } = useAuth();
  const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ old: false, new: false, confirm: false });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        throw new Error('New passwords do not match');
      }
      if (passwordData.newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters long');
      }
      await updateUserPassword(currentUserId, passwordData.oldPassword, passwordData.newPassword);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setSuccess('Password updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });
  };

  return (
    <div className="p-6 space-y-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Profile</h1>
        {error && <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6"><p className="text-red-700">{error}</p></div>}
        {success && <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6"><p className="text-green-700">{success}</p></div>}

        {/* User Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiUser} className="w-5 h-5 mr-2" /> User Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiMail} className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Email Address</p>
                <p className="text-gray-900">{currentUserEmail}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <SafeIcon icon={FiShield} className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-700">Role</p>
                <p className="text-gray-900 capitalize">{currentUserRole?.replace('_', ' ')}</p>
              </div>
            </div>
            {currentTenantName && (
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiBriefcase} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Business</p>
                  <p className="text-gray-900">{currentTenantName}</p>
                </div>
              </div>
            )}
            {currentUser?.display_name && (
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Display Name</p>
                  <p className="text-gray-900">{currentUser.display_name}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <SafeIcon icon={FiLock} className="w-5 h-5 mr-2" /> Change Password
          </h2>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
              <div className="relative">
                <input type={showPasswords.old ? 'text' : 'password'} name="oldPassword" value={passwordData.oldPassword} onChange={handlePasswordChange} required className="w-full pr-12 px-3 py-2 border border-gray-300 rounded-md" />
                <button type="button" onClick={() => togglePasswordVisibility('old')} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"><SafeIcon icon={showPasswords.old ? FiEyeOff : FiEye} className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <input type={showPasswords.new ? 'text' : 'password'} name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} required minLength={6} className="w-full pr-12 px-3 py-2 border border-gray-300 rounded-md" />
                <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"><SafeIcon icon={showPasswords.new ? FiEyeOff : FiEye} className="w-4 h-4" /></button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <div className="relative">
                <input type={showPasswords.confirm ? 'text' : 'password'} name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} required className="w-full pr-12 px-3 py-2 border border-gray-300 rounded-md" />
                <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"><SafeIcon icon={showPasswords.confirm ? FiEyeOff : FiEye} className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex justify-end">
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={isLoading} className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50">
                <SafeIcon icon={FiSave} className="w-4 h-4" /> <span>{isLoading ? 'Updating...' : 'Update Password'}</span>
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default Profile;